const PHOTO_UPLOAD_PATCH = Symbol.for('eInvitation.photoAlbumUploadCompression')
const PHOTO_PRIVACY_UI_PATCH = Symbol.for('eInvitation.photoAlbumPrivacyControls')
const CLIENT_IMAGE_MAX_DIMENSION = 1600
const CLIENT_IMAGE_MAX_BYTES = Math.round(1.5 * 1024 * 1024)
const CLIENT_IMAGE_QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52]

const photoPrivacySelections = new Map()
let webpSupport

function isHeicLike(file) {
  const name = String(file?.name || '').toLowerCase()
  const type = String(file?.type || '').toLowerCase()
  return ['image/heic', 'image/heif', 'image/x-heic', 'image/x-heif'].includes(type) || /\.(heic|heif)$/.test(name)
}

function inferImageType(file) {
  const type = String(file?.type || '').toLowerCase()
  if (type.startsWith('image/')) return type

  const extension = String(file?.name || '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1]
  const typeByExtension = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    heic: 'image/heic',
    heif: 'image/heif',
  }
  return typeByExtension[extension] || ''
}

function installCameraFileTypeNormalizer() {
  if (
    typeof document === 'undefined' ||
    typeof DataTransfer === 'undefined' ||
    typeof File === 'undefined'
  ) return

  document.addEventListener('change', (event) => {
    const input = event.target
    if (!input || input.tagName !== 'INPUT' || input.type !== 'file' || !input.files?.length) return
    if (!String(input.accept || '').toLowerCase().includes('heic') && !input.closest?.('.epa-upload-form')) return

    try {
      const normalized = new DataTransfer()
      let changed = false

      Array.from(input.files).forEach((file) => {
        const inferredType = inferImageType(file)
        if (inferredType && inferredType !== file.type) {
          normalized.items.add(new File([file], file.name, {
            type: inferredType,
            lastModified: file.lastModified || Date.now(),
          }))
          changed = true
        } else {
          normalized.items.add(file)
        }
      })

      if (changed) input.files = normalized.files
    } catch (err) {
      console.warn('Camera file type normalization skipped:', err.message)
    }
  }, true)
}

function getPreviewPrivacyKey(previewItem) {
  const imageName = previewItem.querySelector('img')?.alt || 'photo'
  const siblings = Array.from(previewItem.parentElement?.querySelectorAll('.epa-preview-item') || [])
  const occurrence = siblings
    .slice(0, Math.max(0, siblings.indexOf(previewItem)))
    .filter((item) => (item.querySelector('img')?.alt || 'photo') === imageName)
    .length
  return `${imageName}::${occurrence}`
}

function createPrivacyControl(previewItem) {
  const privacyKey = getPreviewPrivacyKey(previewItem)
  const label = document.createElement('label')
  label.className = 'epa-preview-privacy'
  label.setAttribute('aria-label', 'Photo privacy')

  const text = document.createElement('span')
  text.textContent = 'Privacy'

  const select = document.createElement('select')
  select.className = 'epa-preview-privacy-select'
  select.dataset.privacyKey = privacyKey
  select.setAttribute('aria-label', 'Choose whether this photo is public or private')

  const publicOption = document.createElement('option')
  publicOption.value = 'public'
  publicOption.textContent = 'Public'

  const privateOption = document.createElement('option')
  privateOption.value = 'private'
  privateOption.textContent = 'Private'

  select.append(publicOption, privateOption)
  select.value = photoPrivacySelections.get(privacyKey) || 'public'
  select.addEventListener('change', () => {
    photoPrivacySelections.set(privacyKey, select.value === 'private' ? 'private' : 'public')
  })

  label.append(text, select)
  return label
}

function ensurePhotoPrivacyControls(root = document) {
  if (typeof document === 'undefined') return

  root.querySelectorAll?.('.epa-upload-form .epa-preview-item').forEach((previewItem) => {
    if (previewItem.querySelector('.epa-preview-privacy-select')) return
    previewItem.appendChild(createPrivacyControl(previewItem))
  })
}

function installPhotoPrivacyControls() {
  if (typeof document === 'undefined' || globalThis[PHOTO_PRIVACY_UI_PATCH]) return

  const start = () => {
    ensurePhotoPrivacyControls(document)

    document.addEventListener('click', (event) => {
      if (event.target.closest?.('.epa-clear-files')) photoPrivacySelections.clear()
    }, true)

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return

          if (
            node.matches?.('.epa-form-message-success') ||
            node.querySelector?.('.epa-form-message-success')
          ) {
            photoPrivacySelections.clear()
          }

          if (node.matches?.('.epa-preview-item')) ensurePhotoPrivacyControls(node.parentElement || document)
          else if (node.querySelector?.('.epa-preview-item')) ensurePhotoPrivacyControls(node)
        })
      })
    })

    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  globalThis[PHOTO_PRIVACY_UI_PATCH] = true
}

function collectPhotoPrivacy(formData) {
  const photoCount = Array.from(formData.getAll('photos') || []).length
  if (photoCount === 0) return []
  if (typeof document === 'undefined') return Array(photoCount).fill('public')

  ensurePhotoPrivacyControls(document)
  const values = Array.from(
    document.querySelectorAll('.epa-upload-form .epa-preview-item .epa-preview-privacy-select')
  ).map((select) => {
    const value = select.value === 'private' ? 'private' : 'public'
    if (select.dataset.privacyKey) photoPrivacySelections.set(select.dataset.privacyKey, value)
    return value
  })

  return Array.from({ length: photoCount }, (_unused, index) => values[index] || 'public')
}

function appendPhotoPrivacy(formData) {
  if (!(formData instanceof FormData)) return formData

  const privacy = collectPhotoPrivacy(formData)
  if (privacy.length > 0) {
    formData.set('photoPrivacy', JSON.stringify(privacy))
  }
  return formData
}

function isSafariBrowser() {
  if (typeof navigator === 'undefined') return false
  return /^((?!chrome|chromium|android|crios|fxios|edgios).)*safari/i.test(navigator.userAgent)
}

function browserSupportsWebp() {
  if (webpSupport !== undefined) return webpSupport
  if (typeof document === 'undefined') return false

  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp')
  } catch {
    webpSupport = false
  }

  return webpSupport
}

function isPhotoAlbumUploadRequest(input, init) {
  const isRequest = typeof Request !== 'undefined' && input instanceof Request
  const method = String(init?.method || (isRequest ? input.method : 'GET')).toUpperCase()
  const url = String(isRequest ? input.url : input || '')
  return method === 'POST' && /\/(?:api\/)?photo-albums\/[^/?#]+\/uploads(?:[?#]|$)/.test(url)
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => resolve({
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      cleanup: () => URL.revokeObjectURL(objectUrl),
    })
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error(`The browser could not decode ${file.name || 'this photo'}.`))
    }
    image.src = objectUrl
  })
}

async function loadImageSource(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close(),
      }
    } catch {
      // Safari and some mobile browsers cannot decode every camera format here.
    }
  }

  return loadImageElement(file)
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error(`Unable to encode ${type}.`))
    }, type, quality)
  })
}

async function compressPhotoForUpload(file) {
  if (typeof File === 'undefined' || !(file instanceof File) || !String(file.type || '').startsWith('image/') || isHeicLike(file)) {
    return file
  }

  let loaded
  try {
    loaded = await loadImageSource(file)
    const scale = Math.min(
      1,
      CLIENT_IMAGE_MAX_DIMENSION / Math.max(loaded.width, loaded.height)
    )
    const width = Math.max(1, Math.round(loaded.width * scale))
    const height = Math.max(1, Math.round(loaded.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d', { alpha: true })
    if (!context) return file

    const useWebp = browserSupportsWebp() && !isSafariBrowser()
    const outputType = useWebp ? 'image/webp' : 'image/jpeg'
    const extension = useWebp ? 'webp' : 'jpg'

    if (outputType === 'image/jpeg') {
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
    }
    context.drawImage(loaded.source, 0, 0, width, height)

    let encodedBlob = null
    for (const quality of CLIENT_IMAGE_QUALITY_STEPS) {
      encodedBlob = await canvasToBlob(canvas, outputType, quality)
      if (encodedBlob.size <= CLIENT_IMAGE_MAX_BYTES) break
    }

    if (!encodedBlob || (encodedBlob.size >= file.size && file.size <= CLIENT_IMAGE_MAX_BYTES)) {
      return file
    }

    const baseName = String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo'
    return new File([encodedBlob], `${baseName}.${extension}`, {
      type: outputType,
      lastModified: file.lastModified || Date.now(),
    })
  } catch (err) {
    console.warn('Client photo compression skipped:', err.message)
    return file
  } finally {
    loaded?.cleanup?.()
  }
}

async function compressPhotoAlbumFormData(formData) {
  const optimized = new FormData()

  for (const [key, value] of formData.entries()) {
    if (key === 'photos' && value instanceof File) {
      const compressed = await compressPhotoForUpload(value)
      optimized.append(key, compressed, compressed.name)
    } else {
      optimized.append(key, value)
    }
  }

  return optimized
}

export function installPhotoAlbumUploadCompression() {
  if (typeof globalThis === 'undefined' || typeof globalThis.fetch !== 'function' || typeof FormData === 'undefined') return
  if (globalThis[PHOTO_UPLOAD_PATCH]) return

  const originalFetch = globalThis.fetch.bind(globalThis)

  globalThis.fetch = async (input, init = {}) => {
    if (!isPhotoAlbumUploadRequest(input, init) || !(init.body instanceof FormData)) {
      return originalFetch(input, init)
    }

    const bodyWithPrivacy = appendPhotoPrivacy(init.body)

    try {
      const body = await compressPhotoAlbumFormData(bodyWithPrivacy)
      return originalFetch(input, { ...init, body })
    } catch (err) {
      console.warn('Photo album pre-upload optimization failed; uploading originals:', err.message)
      return originalFetch(input, { ...init, body: bodyWithPrivacy })
    }
  }

  installCameraFileTypeNormalizer()
  installPhotoPrivacyControls()
  globalThis[PHOTO_UPLOAD_PATCH] = true
}
